import { streamingPost } from '@/utils/streamingUtils';
import { buildFetchConfig } from '@/utils/fetchUtils';
import {
  extractAIMessageContent,
  extractStreamContent,
  extractStreamingReasoningStep,
  processUIResourcesFromToolMessage,
  extractAllReasoningSteps,
  calculateReasoningStepsHash,
  formatKnowledgeCitations,
  stripInternalTags,
  extractToolMetaDataFromStream,
  normalizeStreamMessages,
  extractLastToolResultMessage,
} from '@/utils/chatUtils';
import type {
  ChatMessage,
  ReasoningStep,
  ContentBlock,
  PromptRequest,
  FeedbackParams,
  SkillsAppliedEntry,
  ContextAppliedEntry,
} from '@Types/chat';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

export const sendPrompt = async (
  request: PromptRequest,
  onChunk?: (
    content: string,
    extras?: ChatMessage['extras'],
    reasoningSteps?: ReasoningStep[],
    transparencySummary?: ChatMessage['transparencySummary'],
    skillsApplied?: SkillsAppliedEntry[],
    contextApplied?: ContextAppliedEntry[],
  ) => void,
  signal?: AbortSignal,
): Promise<ChatMessage> => {
  const { ...requestBody } = request;
  let allMessages: Array<any> = [];
  let finalExtras: any = {};
  let lastContent = '';
  let lastReasoningStepsHash = '';
  let lastUIResourceCount = 0;
  let streamedContent = '';
  // Track reasoning content per iteration (each AIMessage with tools = one iteration)
  const reasoningContentByIteration: string[] = [];
  let currentIterationContent = '';
  let lastAiMessageWithToolsCount = 0;
  const streamingReasoningSteps: ReasoningStep[] = [];
  let transparencySummary:
    | NonNullable<ChatMessage['transparencySummary']>
    | undefined;
  let skillsApplied: SkillsAppliedEntry[] | undefined;
  let contextApplied: ContextAppliedEntry[] | undefined;
  let streamedUIResources: ReturnType<typeof processUIResourcesFromToolMessage>;
  let streamedReasoningSteps: ReasoningStep[] = [];
  let streamErrorContent = '';
  let lastMessageCount = 0;

  await streamingPost(
    '/prompt',
    requestBody,
    (line: string) => {
      try {
        const chunk = JSON.parse(line);

        if (chunk.extras) {
          finalExtras = chunk.extras;
        }

        // Accumulate reasoning content for current iteration
        const reasoningChunk =
          chunk?.type === 'content_stream' &&
          chunk.content &&
          chunk.is_reasoning
            ? chunk.content
            : '';
        if (reasoningChunk) {
          currentIterationContent += reasoningChunk;
        }

        if (chunk.messages?.length) {
          allMessages = normalizeStreamMessages(chunk.messages);

          // Count AIMessages with tool_calls to detect new iterations
          const aiMessagesWithTools = allMessages.filter(
            (msg: any) =>
              msg?.id?.[msg.id.length - 1] === 'AIMessage' &&
              msg.kwargs?.tool_calls?.length > 0,
          );

          // If we have a new AIMessage with tools, save current reasoning content
          if (aiMessagesWithTools.length > lastAiMessageWithToolsCount) {
            if (currentIterationContent.trim()) {
              reasoningContentByIteration.push(currentIterationContent.trim());
            }
            currentIterationContent = '';
            lastAiMessageWithToolsCount = aiMessagesWithTools.length;
          }
        }

        // Route final content to text block
        streamedContent += extractStreamContent(chunk); // is_final -> text block

        const streamingStep = extractStreamingReasoningStep(chunk);
        if (streamingStep) {
          streamingReasoningSteps.push(streamingStep);
        }

        if (chunk?.type === 'error' && chunk.content) {
          streamErrorContent = String(chunk.content);
        }

        if (
          (chunk?.type === 'context_used' ||
            chunk?.type === 'context_applied') &&
          Array.isArray(chunk.context)
        ) {
          contextApplied = chunk.context;
          if (onChunk) {
            onChunk(
              formatKnowledgeCitations(stripInternalTags(streamedContent)),
              {
                ...finalExtras,
                ...(streamedUIResources && { uiResources: streamedUIResources }),
              },
              streamingReasoningSteps.length ? streamingReasoningSteps : undefined,
              transparencySummary,
              skillsApplied,
              contextApplied,
            );
          }
        }

        if (chunk?.type === 'skills_applied' && Array.isArray(chunk.skills)) {
          skillsApplied = chunk.skills;
          const adp = getBernoulliAdp();
          if (adp) {
            for (const skill of skillsApplied ?? []) {
              trackAdpWaypoint(adp, ADP_EVENTS.SKILL_APPLIED, {
                conversation_id: request.conversation_id || '',
                request_id: request.request_id || '',
                skill_id: skill.skill_id,
                skill_name: skill.name,
                skill_scope: skill.scope ?? 'system',
                trigger_source: skill.auto_triggered
                  ? 'classifier'
                  : 'manual_chip',
              });
            }
          }
        }

        // Handle transparency summary event
        if (chunk?.type === 'transparency_summary') {
          transparencySummary = {
            whatIDid: chunk.what_i_did || '',
            assumptions: chunk.assumptions || '',
            nextPrompts: Array.isArray(chunk.next_prompts)
              ? chunk.next_prompts.filter(
                  (prompt: unknown) =>
                    (typeof prompt === 'string' && prompt.trim().length > 0) ||
                    (typeof prompt === 'object' &&
                      prompt !== null &&
                      'label' in prompt &&
                      'prompt' in prompt),
                )
              : [],
          };
        }

        const uiResources = processUIResourcesFromToolMessage(allMessages);

        // Build reasoning steps: tool info from messages + streamed content per iteration + tool_logs
        const messageReasoningSteps = extractAllReasoningSteps(allMessages);

        // Inject reasoning content into tool steps using a separate iteration counter
        let iterationIdx = 0;
        const toolStepCount = messageReasoningSteps.filter(
          s => s.tools && s.tools.length > 0,
        ).length;
        const reasoningStepsWithContent = messageReasoningSteps.map(step => {
          if (step.tools && step.tools.length > 0) {
            const savedContent = reasoningContentByIteration[iterationIdx];
            const isLastToolStep = iterationIdx === toolStepCount - 1;
            const contentToUse =
              savedContent ||
              (isLastToolStep ? currentIterationContent.trim() : '');

            iterationIdx++;
            if (contentToUse) {
              return { ...step, content: contentToUse };
            }
          }
          return step;
        });

        const reasoningSteps = [
          ...reasoningStepsWithContent,
          ...streamingReasoningSteps,
        ];

        if (uiResources?.length) {
          streamedUIResources = uiResources;
        }
        if (reasoningSteps.length) {
          streamedReasoningSteps = reasoningSteps;
        }

        const contentChanged =
          streamedContent !== lastContent && streamedContent.trim().length > 0;
        const uiResourceCount = uiResources ? uiResources.length : 0;
        const uiResourcesChanged = uiResourceCount > lastUIResourceCount;

        const { reasoningStepsHash, reasoningStepsChanged } =
          calculateReasoningStepsHash(reasoningSteps, lastReasoningStepsHash);

        const transparencySummaryChanged =
          !!transparencySummary &&
          !!(
            transparencySummary.whatIDid ||
            transparencySummary.assumptions ||
            (transparencySummary.nextPrompts &&
              transparencySummary.nextPrompts.length > 0)
          );

        const messagesChanged = allMessages.length > lastMessageCount;

        if (
          (contentChanged ||
            reasoningStepsChanged ||
            uiResourcesChanged ||
            transparencySummaryChanged ||
            messagesChanged) &&
          onChunk
        ) {
          if (messagesChanged) {
            lastMessageCount = allMessages.length;
          }
          lastContent = streamedContent;
          lastReasoningStepsHash = reasoningStepsHash;
          lastUIResourceCount = uiResourceCount;
          onChunk(
            stripInternalTags(streamedContent),
            {
              ...finalExtras,
              ...(uiResources && { uiResources }),
            },
            reasoningSteps,
            transparencySummary,
            skillsApplied,
            contextApplied,
          );
        }
      } catch (e) {
        console.error('[STREAMING] Error parsing chunk:', e);
        console.error('[STREAMING] Failed chunk data:', line);
      }
    },
    undefined,
    signal,
  );

  const normalizedMessages = allMessages;
  const finalUIResources =
    processUIResourcesFromToolMessage(normalizedMessages) ||
    streamedUIResources;
  const toolMetaData = extractToolMetaDataFromStream(normalizedMessages);
  const messageReasoningSteps = extractAllReasoningSteps(normalizedMessages);
  const extractedReasoningSteps =
    messageReasoningSteps.length > 0
      ? messageReasoningSteps
      : streamedReasoningSteps;

  let finalIterationIdx = 0;
  const finalReasoningSteps = extractedReasoningSteps.map(step => {
    if (step.tools && step.tools.length > 0) {
      const savedContent = reasoningContentByIteration[finalIterationIdx];
      finalIterationIdx++;
      if (savedContent) {
        return { ...step, content: savedContent };
      }
    }
    return step;
  });

  const hasToolActivity = finalReasoningSteps.some(
    step => (step.tools && step.tools.length > 0) || step.type === 'tool_log',
  );
  const toolNames = finalExtras?.tool_names;
  const hasExtrasToolActivity =
    Array.isArray(toolNames) && toolNames.length > 0;

  // Agent turns that render MCP-UI (e.g. import validation) or run analysis tools
  // (custom_analysis, get_events) may finish without a final text AIMessage.
  let finalContent =
    extractAIMessageContent(normalizedMessages) ||
    formatKnowledgeCitations(stripInternalTags(streamedContent)) ||
    '';

  if (!finalContent.trim()) {
    if (transparencySummary?.whatIDid?.trim()) {
      finalContent = transparencySummary.whatIDid;
    } else {
      finalContent = extractLastToolResultMessage(normalizedMessages);
    }
  }

  if (!finalContent.trim()) {
    for (let i = finalReasoningSteps.length - 1; i >= 0; i--) {
      const stepContent = finalReasoningSteps[i].content?.trim();
      if (stepContent) {
        finalContent = stepContent;
        break;
      }
    }
  }

  if (!finalContent.trim() && streamErrorContent.trim()) {
    console.error('[CHAT_STREAM_ERROR]', {
      conversation_id: request.conversation_id,
      request_id: request.request_id,
      error: streamErrorContent,
      service_id: 'app-water-ai-bernoulli',
    });
    finalContent =
      'Something went wrong while processing your request. Please try again.';
  }

  const hasStreamedToolActivity = streamedReasoningSteps.some(
    step => (step.tools && step.tools.length > 0) || step.type === 'tool_log',
  );
  const hasToolMessages = normalizedMessages.some(
    msg => msg?.id?.[msg.id.length - 1] === 'ToolMessage',
  );

  const hasDeliverable =
    !!finalContent.trim() ||
    !!finalUIResources?.length ||
    !!streamedUIResources?.length ||
    hasToolActivity ||
    hasStreamedToolActivity ||
    hasExtrasToolActivity ||
    hasToolMessages ||
    !!streamErrorContent.trim() ||
    !!transparencySummary?.whatIDid?.trim();

  if (!hasDeliverable) {
    const debugPayload = {
      conversation_id: request.conversation_id,
      request_id: request.request_id,
      message_count: normalizedMessages.length,
      tool_names: toolNames,
      has_final_content: !!finalContent.trim(),
      ui_resource_count: finalUIResources?.length ?? 0,
      has_tool_activity: hasToolActivity,
      has_streamed_tool_activity: hasStreamedToolActivity,
      has_extras_tool_activity: hasExtrasToolActivity,
      streamed_ui_count: streamedUIResources?.length ?? 0,
      streamed_reasoning_steps: streamedReasoningSteps.length,
      has_tool_messages: hasToolMessages,
      has_stream_error: !!streamErrorContent.trim(),
      service_id: 'app-water-ai-bernoulli',
    };
    console.error('[NO_AI_CONTENT]', JSON.stringify(debugPayload));
    throw new Error('No AI response content found');
  }

  // Tool-only turns (e.g. validate_import_data) may have no text — add a short fallback.
  if (
    !finalContent.trim() &&
    !(finalUIResources?.length || streamedUIResources?.length)
  ) {
    finalContent = 'Validation complete. Review the results above.';
  }

  if (process.env.NODE_ENV === 'development') {
    console.debug('[CHAT_STREAM_RESULT]', {
      request_id: request.request_id,
      content_length: finalContent.trim().length,
      ui_resource_count: finalUIResources?.length ?? 0,
      tool_names: toolNames,
      reasoning_steps: finalReasoningSteps.length,
    });
  }

  const blocks: ContentBlock[] = [];
  if (finalUIResources) {
    blocks.push({ type: 'ui', data: finalUIResources });
  }
  if (finalContent.trim()) {
    blocks.push({ type: 'text', data: finalContent });
  }

  return {
    role: 'assistant',
    content: finalContent,
    timestamp: new Date().toISOString(),
    blocks: blocks.length ? blocks : undefined,
    reasoningSteps: finalReasoningSteps.length
      ? finalReasoningSteps
      : undefined,
    transparencySummary,
    skillsApplied,
    contextApplied,
    extras: {
      ...finalExtras,
      ...(finalUIResources && { uiResources: finalUIResources }),
      ...(toolMetaData.length > 0 && { toolMetaData }),
    },
    messageCompleted: true,
  };
};

export const sendFeedback = async (
  feedback: FeedbackParams,
): Promise<string> => {
  const { baseURL, headers } = buildFetchConfig();
  try {
    await fetch(`${baseURL}/feedbacks`, {
      method: 'POST',
      headers,
      body: JSON.stringify(feedback),
    });

    return 'Thanks for your feedback!';
  } catch (error) {
    console.error('[FEEDBACK] Network error:', error);
    return 'Failed to submit feedback. Please check your connection and try again.';
  }
};
